/** @typedef {{ participantLabel: string, eventName: string, status: string, detail?: string }} RegistrationOutcomeRow */
const CONFIRMED_WHATS_NEXT = [
  "Thank you for registering! Please check your email for your registration confirmation. In that email, you'll find your unique check-in code to bring with you to the event. You can also find your code on the Event Calendar under My Check-In QR Codes.",
  "No further action is needed unless you are contacted by the event organizer.",
].join(" ");

const WAITLIST_WHATS_NEXT =
  "One or more events are at capacity. If a spot opens in an event, you may be moved to registered for that event and receive a separate confirmation email.";

const sectionHeaderStyle = {
  padding: "10px 12px",
  fontWeight: 700,
  fontSize: 13,
  letterSpacing: "0.02em",
  color: "#fff",
};

const noteBoxStyle = (borderColor, bg) => ({
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 6,
  border: `1px solid ${borderColor}`,
  background: bg,
  fontSize: 13,
  lineHeight: 1.45,
  color: "#212529",
});

/** Same note shell for confirmed and waitlisted (unified confirmation). */
const WHATS_NEXT_NOTE_BOX_STYLE = noteBoxStyle("#a3cfbb", "#fff");
const WHATS_NEXT_TITLE_STYLE = {
  fontWeight: 700,
  marginBottom: 6,
  fontSize: 12,
  color: "#146c43",
};

function WhatsNextNote({ children }) {
  return (
    <div style={WHATS_NEXT_NOTE_BOX_STYLE}>
      <div style={WHATS_NEXT_TITLE_STYLE}>What’s next</div>
      <div>{children}</div>
    </div>
  );
}

/** Normalize status / API message so "Registered" does not repeat as extra row text under CONFIRMED. */
function normalizeOutcomeEcho(s) {
  return (s || "").trim().toLowerCase().replace(/_/g, " ");
}

/** Hide row detail when it only repeats the row outcome (e.g. API message "Registered" vs status "registered"). */
function shouldShowOutcomeRowDetail(detail, rowStatus) {
  const d = normalizeOutcomeEcho(detail);
  if (!d) return false;
  return normalizeOutcomeEcho(rowStatus) !== d;
}

/** Email delivery failures are logged for ops review — not shown to end users after a successful registration. */
function isConfirmationEmailDeliveryError(msg) {
  return /confirmation email was not sent/i.test(String(msg || ""));
}

function filterEndUserRegistrationErrors(errors) {
  const list = Array.isArray(errors) ? errors.filter(Boolean) : [];
  const visible = [];
  for (const e of list) {
    if (isConfirmationEmailDeliveryError(e)) {
      console.warn("[Event registration] Confirmation email issue (hidden from user):", e);
      continue;
    }
    visible.push(e);
  }
  return visible;
}

function outcomeListRows(rows) {
  return (
    <ul style={{ margin: "8px 0 0 0", paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
      {rows.map((r, i) => (
        <li key={`${r.participantLabel}-${r.eventName}-${i}`} style={{ marginBottom: 6 }}>
          <strong>{r.participantLabel}</strong>
          <span style={{ color: "#495057" }}> — {r.eventName}</span>
          {r.status === "already_registered" && (
            <span style={{ color: "#6c757d", fontSize: 12 }}> (already registered)</span>
          )}
          {shouldShowOutcomeRowDetail(r.detail, r.status) ? (
            <div style={{ fontSize: 12, color: "#6c757d", marginTop: 2 }}>{r.detail}</div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Shared registration outcome layout for Event Calendar (self and parent): confirmed vs waitlisted sections + notes.
 * @param {{ confirmed: RegistrationOutcomeRow[], waitlisted: RegistrationOutcomeRow[], errors?: string[] }} props
 */
export default function EventRegistrationConfirmationBody({ confirmed, waitlisted, errors }) {
  const conf = Array.isArray(confirmed) ? confirmed : [];
  const wait = Array.isArray(waitlisted) ? waitlisted : [];
  const errs = filterEndUserRegistrationErrors(errors);

  const newConfirmed = conf.filter((r) => (r.status || "").trim() === "registered");
  const alreadyConfirmed = conf.filter((r) => (r.status || "").trim() === "already_registered");
  const slotCount = newConfirmed.length;
  const peopleCount = new Set(conf.map((r) => r.participantLabel)).size;

  const waitSlotCount = wait.length;
  const waitPeopleCount = new Set(wait.map((r) => r.participantLabel)).size;

  return (
    <div style={{ maxHeight: "min(70vh, 520px)", overflowY: "auto", textAlign: "left" }}>
      {conf.length > 0 && (
        <section
          style={{
            marginBottom: 14,
            borderRadius: 8,
            overflow: "hidden",
            border: "1px solid #a3cfbb",
          }}
        >
          <div style={{ ...sectionHeaderStyle, background: "#198754" }}>CONFIRMED REGISTRATIONS</div>
          <div style={{ padding: "12px 14px", background: "#f4fbf6" }}>
            <div style={{ fontSize: 13, color: "#212529" }}>
              {slotCount > 0 && (
                <>
                  {slotCount} slot{slotCount === 1 ? "" : "s"} confirmed
                  {peopleCount > 0 ? ` for ${peopleCount} participant${peopleCount === 1 ? "" : "s"}` : ""}.
                </>
              )}
              {slotCount === 0 && alreadyConfirmed.length > 0 && (
                <>No new confirmations; existing registration(s) unchanged.</>
              )}
              {slotCount === 0 && alreadyConfirmed.length === 0 && <>Registration details below.</>}
            </div>
            {outcomeListRows(conf)}
            <WhatsNextNote>{CONFIRMED_WHATS_NEXT}</WhatsNextNote>
          </div>
        </section>
      )}

      {wait.length > 0 && (
        <section
          style={{
            marginBottom: 14,
            borderRadius: 8,
            overflow: "hidden",
            border: "1px solid #e0b150",
          }}
        >
          <div style={{ ...sectionHeaderStyle, background: "#e8a317" }}>WAITLISTED REGISTRATIONS</div>
          <div style={{ padding: "12px 14px", background: "#fffbeb" }}>
            <div style={{ fontSize: 13, color: "#212529" }}>
              {waitSlotCount > 0 && (
                <>
                  {waitSlotCount} slot{waitSlotCount === 1 ? "" : "s"} waitlisted
                  {waitPeopleCount > 0
                    ? ` for ${waitPeopleCount} participant${waitPeopleCount === 1 ? "" : "s"}`
                    : ""}
                  .
                </>
              )}
            </div>
            {outcomeListRows(wait)}
            <WhatsNextNote>{WAITLIST_WHATS_NEXT}</WhatsNextNote>
          </div>
        </section>
      )}

      {errs.length > 0 && (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 6,
            border: "1px solid #f5c2c7",
            background: "#f8d7da",
            color: "#842029",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <strong>Issues</strong>
          <ul style={{ margin: "8px 0 0 0", paddingLeft: 18 }}>
            {errs.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Map API `results` items into row objects for {@link EventRegistrationConfirmationBody}.
 * @param {Array<object>} results
 * @param {string} [defaultEventName]
 */
export function mapParentApiResultsToOutcomeRows(results, defaultEventName = "Event") {
  const confirmed = [];
  const waitlisted = [];
  const errors = [];
  for (const r of results || []) {
    const status = (r?.status || "").trim();
    const eventName = (r?._eventName || defaultEventName || "Event").trim();
    const rawChild = (r?.childName || "").trim() || "Child";
    const participantLabel = /^child\s*:/i.test(rawChild) ? rawChild : `Child: ${rawChild}`;
    const msg = (r?.message || "").trim();
    const rowDetail =
      msg && normalizeOutcomeEcho(msg) !== normalizeOutcomeEcho(status) ? msg : "";
    const row = { participantLabel, eventName, status, detail: rowDetail };
    if (status === "waitlisted") waitlisted.push(row);
    else if (status === "registered" || status === "already_registered") confirmed.push(row);
    else errors.push(`${participantLabel} — ${eventName}: ${rowDetail || status || "Error"}`);
  }
  return { confirmed, waitlisted, errors };
}
